import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Camera, CameraOff, Loader2, ArrowLeft, AlertTriangle } from "lucide-react";
import { uploadLiveFrame } from "@/lib/api";

export default function LiveSender() {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const intervalRef = useRef(null);
  const [running, setRunning] = useState(false);
  const [starting, setStarting] = useState(false);
  const [count, setCount] = useState(0);
  const [error, setError] = useState("");

  const capture = async () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !video.videoWidth) return;
    const w = 640;
    const h = Math.round((video.videoHeight / video.videoWidth) * w) || 360;
    canvas.width = w;
    canvas.height = h;
    canvas.getContext("2d").drawImage(video, 0, 0, w, h);
    canvas.toBlob(async (blob) => {
      if (!blob) return;
      try {
        await uploadLiveFrame(blob);
        setCount((c) => c + 1);
        setError("");
      } catch {
        setError("Senden fehlgeschlagen – bitte Anmeldung & Verbindung prüfen.");
      }
    }, "image/jpeg", 0.55);
  };

  const start = async () => {
    setStarting(true);
    setError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment", width: { ideal: 1280 } },
        audio: false,
      });
      streamRef.current = stream;
      videoRef.current.srcObject = stream;
      await videoRef.current.play();
      setRunning(true);
      setTimeout(capture, 1000);
      intervalRef.current = setInterval(capture, 5000);
    } catch {
      setError("Kamerazugriff nicht möglich. Bitte Kamera erlauben und HTTPS verwenden.");
    } finally {
      setStarting(false);
    }
  };

  const stop = () => {
    clearInterval(intervalRef.current);
    streamRef.current?.getTracks().forEach((t) => t.stop());
    setRunning(false);
  };

  useEffect(() => () => stop(), []);

  return (
    <div data-testid="live-sender-page" className="min-h-screen bg-ink text-cream flex flex-col">
      <header className="border-b border-cream/10 px-6 py-4 flex items-center justify-between">
        <div>
          <p className="font-serif text-xl">Live-Sender · Küche</p>
          <p className="text-xs text-cream/50">Dieses Gerät sendet das Live-Bild an die Website.</p>
        </div>
        <Link to="/admin" className="text-cream/60 hover:text-gold text-xs uppercase tracking-[0.15em] inline-flex items-center gap-1">
          <ArrowLeft size={14} /> Admin
        </Link>
      </header>

      <main className="flex-1 max-w-2xl w-full mx-auto px-6 py-8 space-y-6">
        <div className="relative overflow-hidden rounded-xl border border-gold/30 bg-black aspect-video">
          <video ref={videoRef} muted playsInline className="w-full h-full object-cover" />
          {!running && (
            <div className="absolute inset-0 flex items-center justify-center text-cream/50 text-sm">
              Kamera-Vorschau erscheint hier
            </div>
          )}
          {running && (
            <span className="absolute top-3 left-3 inline-flex items-center gap-2 bg-wine/90 text-cream text-xs uppercase tracking-[0.2em] px-3 py-1.5 rounded-full">
              <span className="w-2 h-2 rounded-full bg-red-400 animate-pulse" /> Sendet
            </span>
          )}
        </div>
        <canvas ref={canvasRef} className="hidden" />

        {error && (
          <div className="flex items-start gap-2 text-sm text-red-300 bg-red-900/30 border border-red-500/30 rounded p-3" data-testid="live-sender-error">
            <AlertTriangle size={18} className="shrink-0 mt-0.5" /> {error}
          </div>
        )}

        {!running ? (
          <button onClick={start} disabled={starting} data-testid="live-start-btn"
            className="btn-pop w-full bg-gold text-ink hover:bg-gold-dark px-6 py-4 uppercase text-xs tracking-[0.25em] inline-flex items-center justify-center gap-2 disabled:opacity-60">
            {starting ? <Loader2 size={18} className="animate-spin" /> : <Camera size={18} />} Live starten
          </button>
        ) : (
          <button onClick={stop} data-testid="live-stop-btn"
            className="btn-pop w-full bg-wine text-cream hover:bg-wine-dark px-6 py-4 uppercase text-xs tracking-[0.25em] inline-flex items-center justify-center gap-2">
            <CameraOff size={18} /> Live stoppen
          </button>
        )}

        {running && (
          <p className="text-center text-xs text-cream/50" data-testid="live-frame-count">
            Gesendete Bilder: {count} · alle 5 Sek. · Bildschirm angeschaltet lassen
          </p>
        )}

        <div className="text-xs text-cream/40 space-y-2 border-t border-cream/10 pt-5">
          <p>• Nur den Pizzaofen ins Bild nehmen – keine Gäste oder Mitarbeiter (DSGVO).</p>
          <p>• Gerät ins WLAN & ans Ladegerät, Bildschirm-Timeout deaktivieren.</p>
          <p>• Es wird kein Ton übertragen und nichts gespeichert.</p>
        </div>
      </main>
    </div>
  );
}
